import { notifyError, notifyValidationError } from 'components/common/Toast/Toast';
import type { ZodFieldError } from 'pages/MedicationTemplates/medicationTemplates.validation';
import {
  isZodValidationMessage,
  parseZodMessage
} from 'pages/MedicationTemplates/medicationTemplates.validation';

import type {
  ShopBlock,
  ShopBlockType,
  ShopPage,
  ShopPageDocument,
  ShopPageSummary,
  ShopUnsupportedBlock
} from './shopPages.types';

const supportedBlockTypes = new Set([
  'accordion',
  'featureCard',
  'hero',
  'productGrid',
  'productSpotlight',
  'richText',
  'unsupported'
]);

const authorableBlockTypes = new Set<ShopBlockType>([
  'accordion',
  'featureCard',
  'hero',
  'productGrid',
  'productSpotlight',
  'richText'
]);

export const isCrossClientShopComponent = (entry: {
  defaultWidth?: unknown;
  deliveryClients?: unknown;
  type?: unknown;
}): entry is {
  defaultWidth: 'full' | 'half';
  deliveryClients: ('nativeMobile' | 'patientWeb')[];
  type: ShopBlockType;
} => {
  if (typeof entry.type !== 'string' || !authorableBlockTypes.has(entry.type as ShopBlockType)) {
    return false;
  }
  if (!Array.isArray(entry.deliveryClients)) return false;

  const expectedWidth = entry.type === 'featureCard' ? 'half' : 'full';
  return (
    entry.defaultWidth === expectedWidth &&
    entry.deliveryClients.includes('patientWeb') &&
    entry.deliveryClients.includes('nativeMobile')
  );
};

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const omitNullishValues = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(omitNullishValues);
  if (!value || typeof value !== 'object') return value;

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, nestedValue]) => nestedValue != null)
      .map(([key, nestedValue]) => [key, omitNullishValues(nestedValue)])
  );
};

/**
 * Converts unknown Contentful components into a visible, non-destructive placeholder.
 * The untouched source payload remains available for the next write.
 */
export const normalizeShopBlock = (block: ShopBlock | Record<string, unknown>): ShopBlock => {
  const blockRecord = block as unknown as Record<string, unknown>;
  const type = typeof blockRecord.type === 'string' ? blockRecord.type : 'unknown';
  if (type === 'productGrid' && supportedBlockTypes.has(type)) {
    return {
      ...(omitNullishValues(blockRecord) as ShopBlock),
      productReferences: Array.isArray(blockRecord.productReferences)
        ? blockRecord.productReferences
        : [],
      source: blockRecord.source === 'category' ? 'category' : 'manual'
    } as ShopBlock;
  }
  if (supportedBlockTypes.has(type)) return omitNullishValues(blockRecord) as ShopBlock;

  return {
    contentfulAssets: Array.isArray(blockRecord.contentfulAssets)
      ? blockRecord.contentfulAssets
      : undefined,
    displayName:
      typeof blockRecord.displayName === 'string'
        ? blockRecord.displayName
        : `Contentful ${type} component`,
    excludeSegmentIds: asStringArray(blockRecord.excludeSegmentIds),
    id: typeof blockRecord.id === 'string' ? blockRecord.id : crypto.randomUUID(),
    includeSegmentIds: asStringArray(blockRecord.includeSegmentIds),
    originalType: type,
    preservedData: structuredClone(blockRecord),
    type: 'unsupported'
  } satisfies ShopUnsupportedBlock;
};

export type ShopPageApiShape = Omit<Partial<ShopPage>, 'blocks'> & {
  blocks?: ShopBlock[];
  document?: Partial<ShopPageDocument>;
};

const emptyContentfulMetadata: ShopPage['contentful'] = {
  entryId: null,
  environmentId: null,
  publishedVersion: null,
  updatedAt: null,
  version: null
};

const emptySyncMetadata: ShopPage['sync'] = {
  conflict: null,
  direction: null,
  error: null,
  lastAttemptedAt: null,
  lastSyncedAt: null,
  status: 'notLinked'
};

/**
 * Accepts the nested hybrid API document and the earlier flat proof-of-concept
 * response while backend and frontend branches are being exercised together.
 */
export const normalizeShopPage = (page: ShopPageApiShape): ShopPage => {
  const document = page.document ?? page;
  const blocks = document.blocks ?? page.blocks ?? [];
  // Omit the raw nested `document` so the normalized page does not leak the
  // untransformed API payload alongside the flattened fields.
  const { document: _document, ...pageMetadata } = page;

  return {
    ...pageMetadata,
    _id: page._id ?? '',
    blocks: blocks.map((block) => normalizeShopBlock(block)),
    contentful: { ...emptyContentfulMetadata, ...page.contentful },
    createdAt: page.createdAt ?? '',
    deletedAt: page.deletedAt ?? null,
    excludeSegmentIds: document.excludeSegmentIds ?? [],
    hasUnpublishedChanges: page.hasUnpublishedChanges ?? false,
    includeSegmentIds: document.includeSegmentIds ?? [],
    internalName: document.internalName ?? '',
    lifecycle: page.lifecycle ?? 'active',
    publishedRevision: page.publishedRevision ?? null,
    publishedSlug: page.publishedSlug ?? null,
    recovery: {
      canDetach: false,
      canFinishDeletion: false,
      canRestoreDeletedPage: false,
      detachBlockedReason: null,
      ...page.recovery
    },
    revision: page.revision ?? 0,
    slug: document.slug ?? '',
    source: page.source === 'contentful' ? 'contentful' : 'native',
    status: page.status ?? 'draft',
    sync: { ...emptySyncMetadata, ...page.sync },
    title: document.title ?? '',
    updatedAt: page.updatedAt ?? ''
  };
};

export const normalizeShopPageSummary = (page: ShopPageApiShape): ShopPageSummary => {
  const { blocks: _blocks, ...summary } = normalizeShopPage(page);
  return summary;
};

/** Conflict resolution requires the detail payload captured by the backend. */
export const canReviewShopPageConflict = (page: Pick<ShopPageSummary, 'sync'>): boolean =>
  page.sync.status === 'conflict' && Boolean(page.sync.conflict);

/** The backend rejects ordinary pulls while a conflict is awaiting review. */
export const canPullShopPageFromContentful = (
  page: Pick<ShopPageSummary, 'contentful' | 'lifecycle' | 'sync'>
): boolean =>
  page.lifecycle === 'active' &&
  Boolean(page.contentful.entryId) &&
  page.sync.status !== 'conflict';

const serializeShopBlock = (block: ShopBlock): Record<string, unknown> => {
  if (block.type === 'unsupported') return block.preservedData;
  if (block.type === 'productGrid' && block.source === 'category') {
    const { productReferences: _productReferences, ...categoryCollection } = block;
    return categoryCollection;
  }
  return { ...block };
};

/**
 * Restores unsupported blocks to their original payload before persistence so
 * editing a supported neighbor cannot discard data owned by Contentful.
 */
export const serializeShopPageDocument = (
  document: ShopPageDocument
): Omit<ShopPageDocument, 'blocks'> & { blocks: Record<string, unknown>[] } => ({
  ...document,
  blocks: document.blocks.map(serializeShopBlock)
});

/** Returns a useful message only for revision or Contentful divergence errors. */
export const getShopPageConflictMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('status' in error) || error.status !== 409) {
    return undefined;
  }

  const errorData = 'data' in error ? error.data : undefined;
  if (errorData && typeof errorData === 'object') {
    const detail = 'data' in errorData ? errorData.data : undefined;
    const code =
      detail && typeof detail === 'object' && 'code' in detail && typeof detail.code === 'string'
        ? detail.code
        : undefined;
    // Retrying a failure that originated from a Contentful read is pointless —
    // the remedy is a pull. That remedy outranks whatever prose the backend
    // attached, unlike the codes below where the raw message stays first.
    if (code === 'SHOP_CONTENT_SYNC_PULL_REQUIRED') {
      return 'This failure happened while reading from Contentful. Use "Pull latest" to retry it.';
    }
    // Publishing is refused outright while a conflict is open — the remedy is
    // the "Review conflict" flow, so name it even when a stale client missed
    // the disabled Publish button. The mapped copy outranks the backend prose.
    if (code === 'SHOP_PAGE_PUBLISH_BLOCKED_BY_CONFLICT') {
      return 'This page has an open Contentful conflict. Open "Review conflict", choose a resolution, then publish.';
    }
    // Unpublishing an active page is refused the same way while a conflict is
    // open — the named exit is the "Review conflict" flow, so point at it even
    // when a stale client missed the disabled Unpublish button.
    if (code === 'SHOP_PAGE_UNPUBLISH_BLOCKED_BY_CONFLICT_REVIEW') {
      return 'This page has an open Contentful conflict. Open "Review conflict", choose a resolution, then unpublish.';
    }
    if ('message' in errorData && typeof errorData.message === 'string') return errorData.message;
    if (code === 'SHOP_PAGE_REVISION_CONFLICT') {
      return 'This page changed after you opened it. Refresh the page before saving again.';
    }
    if (code === 'SHOP_CONTENT_SYNC_CONFLICT') {
      return 'Contentful and the Physician Portal both changed. Refresh and review the available recovery options.';
    }
  }

  return 'This page changed in another authoring system. Refresh and review the available recovery options.';
};

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (error && typeof error === 'object' && 'data' in error) {
    const errorData = error.data;
    if (
      errorData &&
      typeof errorData === 'object' &&
      'message' in errorData &&
      typeof errorData.message === 'string' &&
      errorData.message
    ) {
      return errorData.message;
    }
  }

  return undefined;
};

/** "['blocks', 0, 'mediaUrl']" → "blocks[0].mediaUrl" — the path style authors see in summaries. */
const formatZodIssuePath = (path: unknown[]): string =>
  path.reduce<string>((formatted, segment) => {
    if (typeof segment === 'number') return `${formatted}[${segment}]`;
    return formatted ? `${formatted}.${String(segment)}` : String(segment);
  }, '');

/**
 * Shop endpoints reject schema-invalid documents with a JSON-stringified Zod
 * issue array ([{code, path, message}, …]) in the message field. Parse it only
 * when every element matches that shape so prose that merely starts with "["
 * stays verbatim.
 */
const parseZodIssueArray = (message: string): ZodFieldError[] | undefined => {
  if (!message.startsWith('[')) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(message);
  } catch {
    return undefined;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return undefined;

  const issues = parsed.filter(
    (issue): issue is { message: string; path: unknown[] } =>
      Boolean(issue) &&
      typeof issue === 'object' &&
      typeof (issue as { message?: unknown }).message === 'string' &&
      Array.isArray((issue as { path?: unknown }).path)
  );
  if (issues.length !== parsed.length) return undefined;

  return issues.map((issue) => ({
    field: formatZodIssuePath(issue.path) || 'document',
    reason: issue.message
  }));
};

const parseValidationIssuesFromMessage = (message: string): ZodFieldError[] | undefined => {
  const jsonIssues = parseZodIssueArray(message);
  if (jsonIssues) return jsonIssues;
  // Reuses the medication-templates helpers for the older
  // "field: expected …, received …; field: …" server format.
  if (isZodValidationMessage(message)) return parseZodMessage(message);
  return undefined;
};

/**
 * Field errors extracted from a rejected shop mutation whose backend message
 * is a Zod validation payload, or `undefined` for every other failure.
 */
export const getShopPageValidationIssues = (error: unknown): ZodFieldError[] | undefined => {
  const message = getApiErrorMessage(error);
  return message ? parseValidationIssuesFromMessage(message) : undefined;
};

const MAX_SUMMARIZED_ISSUES = 3;

const summarizeValidationIssues = (issues: ZodFieldError[]): string => {
  const visible = issues
    .slice(0, MAX_SUMMARIZED_ISSUES)
    .map(({ field, reason }) => (reason ? `${field} — ${reason}` : field));
  const overflow = issues.length - MAX_SUMMARIZED_ISSUES;
  return `Validation failed: ${visible.join('; ')}${overflow > 0 ? ` (+${overflow} more)` : ''}`;
};

/**
 * Resolves the message shown for a failed shop-page mutation. Shop-page
 * endpoints are excluded from the global error-catching middleware so the
 * feature can own the presentation: a revision/sync conflict explanation
 * first, a compact human summary of Zod validation payloads next, then the
 * backend message, then the caller's fallback so a network error or an empty
 * 500 never fails silently.
 */
export const getShopPageActionErrorMessage = (error: unknown, fallback: string): string => {
  const conflictMessage = getShopPageConflictMessage(error);
  if (conflictMessage) return conflictMessage;

  const message = getApiErrorMessage(error);
  if (message) {
    const issues = parseValidationIssuesFromMessage(message);
    return issues ? summarizeValidationIssues(issues) : message;
  }

  return fallback;
};

/**
 * Uniform toast for rejected shop mutations: validation payloads get the
 * persistent field-by-field toast (they are long and actionable), everything
 * else gets a plain error toast. Routing call sites pass
 * `getShopRoutingActionErrorMessage` to map canonical routing codes first.
 */
export const notifyShopActionError = (
  error: unknown,
  fallback: string,
  resolveMessage: (error: unknown, fallback: string) => string = getShopPageActionErrorMessage
): void => {
  const issues = getShopPageValidationIssues(error);
  if (issues && issues.length > 0) {
    notifyValidationError(issues);
    return;
  }
  notifyError(resolveMessage(error, fallback));
};
